/**
 * @file: context.tsx
 *
 * The chat-room conversation context. `ChatConversationProvider` (see `./provider`)
 * owns the conversation's SHARED state and the live WebSocket subscription;
 * each piece of the conversation tree reads what it needs via `useChatConversation()`.
 *
 * Two layers are exposed: the DOMAIN subjects/actions (messages, send, reply
 * target, members, read-by) and the `coordinator` — the UI-coordination
 * object (scroll/list, image preview, bubble interactions). Everything is a
 * stable reference (BehaviorSubjects + memoized objects), so the value
 * re-creates only on the one-shot bare→baked handle materialisation, and a
 * consumer re-renders only on the subject slice it subscribes to.
 */

import {
  ChatAudioToSend,
  ChatFileToSend,
} from '@euler/app/flows/chat/lib/conversation/hooks/useChatSendPipeline';
import { ChatMediaUploadAsset } from '@euler/app/flows/chat/utils/uploadChatMedia';
import {
  ChatMediasLayout,
  ChatMsg,
  ChatRoomActiveMember,
  ChatUserMessagePayload,
} from '@euler/model/chat';
import { createContext, useContext } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatConversationHandle } from './handle';
import { ConversationCoordinator } from './hooks/useConversationCoordinator';

export interface ChatConversationContextValue {
  // ── room handle / header
  /**
   * A handle to the room — a `bare`|`baked` discriminated union. Raw value
   * (changes at most once per conversation, on materialise). Consumers branch
   * on it (`isBaked` / `isGroupRoom` / `roomTitle`).
   */
  conversationHandle: ChatConversationHandle;

  // ── messages (domain)
  messages$: BehaviorSubject<ChatMsg[]>;
  loading$: BehaviorSubject<boolean>;
  /** True while an older page is being fetched (drives the list footer). */
  loadingOlder$: BehaviorSubject<boolean>;
  /** Load the next older page (infinite scroll toward the top of history). */
  loadOlder: () => void;

  // ── send pipeline (domain) — composer-initiated sends
  sendPayload: (
    payload: ChatUserMessagePayload,
    opts?: { quote?: boolean },
  ) => Promise<boolean>;
  sendMediaAssets: (
    assets: ChatMediaUploadAsset[],
    opts?: { layout?: ChatMediasLayout },
  ) => Promise<void>;
  sendMediaSeparately: (assets: ChatMediaUploadAsset[]) => Promise<void>;
  sendFile: (file: ChatFileToSend) => Promise<void>;
  sendAudio: (audio: ChatAudioToSend) => Promise<void>;

  // ── members (domain) — the single source of truth; consumers derive the
  // count / mention candidates / any slice from it on demand
  members$: BehaviorSubject<ChatRoomActiveMember[]>;

  // ── UI coordination (scroll/list, image preview, read-by, interactions)
  coordinator: ConversationCoordinator;
}

export const ChatConversationContext =
  createContext<ChatConversationContextValue | null>(null);

export function useChatConversation(): ChatConversationContextValue {
  const ctx = useContext(ChatConversationContext);
  if (!ctx) {
    throw new Error(
      'useChatConversation must be used within a ChatConversationProvider',
    );
  }
  return ctx;
}
